import { useState, useEffect } from 'react';
import { Save, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingAbout, setUploadingAbout] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload/logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Cloudinary returns full URL, use it directly
      const logoUrl = response.data.url;
      setSettings({...settings, logo_url: logoUrl});
      toast.success('Logo uploadé');
    } catch (error) {
      toast.error('Erreur lors de l\'upload du logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    setUploadingHero(true);
    const formDataObj = new FormData();
    formDataObj.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload/hero-image`, formDataObj, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Cloudinary returns full URL, use it directly
      const imageUrl = response.data.url;
      setSettings({...settings, hero_image_url: imageUrl});
      toast.success('Image d\'accueil uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploadingHero(false);
    }
  };

  const handleAboutImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    setUploadingAbout(true);
    const formDataObj = new FormData();
    formDataObj.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload/about-image`, formDataObj, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Cloudinary returns full URL, use it directly
      const imageUrl = response.data.url;
      setSettings({...settings, about_image_url: imageUrl});
      toast.success('Image "À propos" uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploadingAbout(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');

    try {
      await axios.put(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres sauvegardés avec succès');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="loading-spinner" className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-settings-page" className="min-h-screen pt-20 pb-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-5xl mx-auto space-y-8">
        <div className="pt-12 flex items-center justify-between">
          <div className="space-y-4">
            <h1 className="text-5xl md:text-6xl font-serif tracking-tight leading-none">Paramètres du Site</h1>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
              Gérez le contenu et les informations de votre site web
            </p>
          </div>

          <Button
            data-testid="save-settings-button"
            onClick={handleSave}
            disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 py-5 text-xs uppercase tracking-widest font-semibold transition-all duration-300"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>

        {/* Section Général */}
        <div className="bg-white border border-border p-6 space-y-6">
          <h2 className="text-2xl font-serif tracking-tight border-b border-border pb-3">Général</h2>
          
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
              Nom de l'agence
            </label>
            <Input
              data-testid="settings-agency-name"
              value={settings.agency_name}
              onChange={(e) => setSettings({...settings, agency_name: e.target.value})}
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
              Logo de l'agence
            </label>
            <div className="flex items-center gap-4">
              {settings.logo_url && (
                <img src={settings.logo_url} alt="Logo" className="h-16 object-contain" />
              )}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                  disabled={uploadingLogo}
                />
                <label htmlFor="logo-upload">
                  <Button
                    type="button"
                    disabled={uploadingLogo}
                    className="cursor-pointer"
                    onClick={() => document.getElementById('logo-upload').click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingLogo ? 'Upload...' : 'Changer le logo'}
                  </Button>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Section Page d'accueil */}
        <div className="bg-white border border-border p-6 space-y-6">
          <h2 className="text-2xl font-serif tracking-tight border-b border-border pb-3">Page d'accueil</h2>
          
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
              Image d'arrière-plan (Hero)
            </label>
            <div className="space-y-4">
              {settings.hero_image_url && (
                <div className="relative w-full h-48 overflow-hidden rounded">
                  <img src={settings.hero_image_url} alt="Hero background" className="w-full h-full object-cover" />
                </div>
              )}
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleHeroImageUpload}
                  className="hidden"
                  id="hero-upload"
                  disabled={uploadingHero}
                />
                <label htmlFor="hero-upload">
                  <Button
                    type="button"
                    disabled={uploadingHero}
                    className="cursor-pointer"
                    onClick={() => document.getElementById('hero-upload').click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingHero ? 'Upload...' : 'Changer l\'image d\'accueil'}
                  </Button>
                </label>
                <p className="text-xs text-muted-foreground mt-2">
                  Recommandé: 1920x1080px minimum, format JPG ou PNG
                </p>
              </div>
            </div>
          </div>
          
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
              Titre Hero
            </label>
            <Input
              data-testid="settings-hero-title"
              value={settings.hero_title}
              onChange={(e) => setSettings({...settings, hero_title: e.target.value})}
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
              Sous-titre Hero
            </label>
            <Textarea
              data-testid="settings-hero-subtitle"
              value={settings.hero_subtitle}
              onChange={(e) => setSettings({...settings, hero_subtitle: e.target.value})}
              rows={2}
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
              Titre "À propos"
            </label>
            <Input
              data-testid="settings-about-title"
              value={settings.about_title}
              onChange={(e) => setSettings({...settings, about_title: e.target.value})}
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
              Description "À propos"
            </label>
            <Textarea
              data-testid="settings-about-description"
              value={settings.about_description}
              onChange={(e) => setSettings({...settings, about_description: e.target.value})}
              rows={4}
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
              Image "À propos"
            </label>
            <div className="space-y-4">
              {settings.about_image_url && (
                <div className="relative w-full h-48 overflow-hidden rounded">
                  <img src={settings.about_image_url} alt="About section" className="w-full h-full object-cover" />
                </div>
              )}
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAboutImageUpload}
                  className="hidden"
                  id="about-upload"
                  disabled={uploadingAbout}
                />
                <label htmlFor="about-upload">
                  <Button
                    type="button"
                    disabled={uploadingAbout}
                    className="cursor-pointer"
                    onClick={() => document.getElementById('about-upload').click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingAbout ? 'Upload...' : 'Changer l\'image "À propos"'}
                  </Button>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Section Nos Services */}
        <div className="bg-white border border-border p-6 space-y-6">
          <h2 className="text-2xl font-serif tracking-tight border-b border-border pb-3">Nos Services (Page d'accueil)</h2>
          
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Service 1</h3>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Titre</label>
              <Input
                value={settings.service1_title || ''}
                onChange={(e) => setSettings({...settings, service1_title: e.target.value})}
                placeholder="Ex: Achat & Vente"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Description</label>
              <Textarea
                value={settings.service1_description || ''}
                onChange={(e) => setSettings({...settings, service1_description: e.target.value})}
                rows={2}
              />
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-border">
            <h3 className="text-lg font-semibold">Service 2</h3>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Titre</label>
              <Input
                value={settings.service2_title || ''}
                onChange={(e) => setSettings({...settings, service2_title: e.target.value})}
                placeholder="Ex: Gestion Locative"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Description</label>
              <Textarea
                value={settings.service2_description || ''}
                onChange={(e) => setSettings({...settings, service2_description: e.target.value})}
                rows={2}
              />
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-border">
            <h3 className="text-lg font-semibold">Service 3</h3>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Titre</label>
              <Input
                value={settings.service3_title || ''}
                onChange={(e) => setSettings({...settings, service3_title: e.target.value})}
                placeholder="Ex: Expertise"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Description</label>
              <Textarea
                value={settings.service3_description || ''}
                onChange={(e) => setSettings({...settings, service3_description: e.target.value})}
                rows={2}
              />
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-border">
            <h3 className="text-lg font-semibold">Service 4</h3>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Titre</label>
              <Input
                value={settings.service4_title || ''}
                onChange={(e) => setSettings({...settings, service4_title: e.target.value})}
                placeholder="Ex: Excellence"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Description</label>
              <Textarea
                value={settings.service4_description || ''}
                onChange={(e) => setSettings({...settings, service4_description: e.target.value})}
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Section Page Gestion Locative */}
        <div className="bg-white border border-border p-6 space-y-6">
          <h2 className="text-2xl font-serif tracking-tight border-b border-border pb-3">Page Gestion Locative</h2>
          
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Titre principal</label>
            <Input
              value={settings.rental_hero_title || ''}
              onChange={(e) => setSettings({...settings, rental_hero_title: e.target.value})}
              placeholder="Ex: Gestion Locative"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Sous-titre</label>
            <Textarea
              value={settings.rental_hero_subtitle || ''}
              onChange={(e) => setSettings({...settings, rental_hero_subtitle: e.target.value})}
              rows={2}
            />
          </div>

          <div className="space-y-4 pt-4 border-t border-border">
            <h3 className="text-lg font-semibold">Fonctionnalité 1</h3>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Titre</label>
              <Input
                value={settings.rental_feature1_title || ''}
                onChange={(e) => setSettings({...settings, rental_feature1_title: e.target.value})}
                placeholder="Ex: Sécurité"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Description</label>
              <Textarea
                value={settings.rental_feature1_description || ''}
                onChange={(e) => setSettings({...settings, rental_feature1_description: e.target.value})}
                rows={2}
              />
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-border">
            <h3 className="text-lg font-semibold">Fonctionnalité 2</h3>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Titre</label>
              <Input
                value={settings.rental_feature2_title || ''}
                onChange={(e) => setSettings({...settings, rental_feature2_title: e.target.value})}
                placeholder="Ex: Documents"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Description</label>
              <Textarea
                value={settings.rental_feature2_description || ''}
                onChange={(e) => setSettings({...settings, rental_feature2_description: e.target.value})}
                rows={2}
              />
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-border">
            <h3 className="text-lg font-semibold">Fonctionnalité 3</h3>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Titre</label>
              <Input
                value={settings.rental_feature3_title || ''}
                onChange={(e) => setSettings({...settings, rental_feature3_title: e.target.value})}
                placeholder="Ex: Paiements"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Description</label>
              <Textarea
                value={settings.rental_feature3_description || ''}
                onChange={(e) => setSettings({...settings, rental_feature3_description: e.target.value})}
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Section Contact */}
        <div className="bg-white border border-border p-6 space-y-6">
          <h2 className="text-2xl font-serif tracking-tight border-b border-border pb-3">Contact</h2>
          
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
              Adresse
            </label>
            <Input
              data-testid="settings-contact-address"
              value={settings.contact_address}
              onChange={(e) => setSettings({...settings, contact_address: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
                Téléphone
              </label>
              <Input
                data-testid="settings-contact-phone"
                value={settings.contact_phone}
                onChange={(e) => setSettings({...settings, contact_phone: e.target.value})}
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
                Email
              </label>
              <Input
                data-testid="settings-contact-email"
                type="email"
                value={settings.contact_email}
                onChange={(e) => setSettings({...settings, contact_email: e.target.value})}
              />
            </div>
          </div>
        </div>

        {/* Section Mentions Légales */}
        <div className="bg-white border border-border p-6 space-y-6">
          <h2 className="text-2xl font-serif tracking-tight border-b border-border pb-3">Mentions Légales</h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
                Forme juridique
              </label>
              <Input
                data-testid="settings-legal-form"
                value={settings.legal_company_form}
                onChange={(e) => setSettings({...settings, legal_company_form: e.target.value})}
                placeholder="SPRL, SA, etc."
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
                Numéro BCE
              </label>
              <Input
                data-testid="settings-legal-bce"
                value={settings.legal_bce_number}
                onChange={(e) => setSettings({...settings, legal_bce_number: e.target.value})}
                placeholder="BE 0123.456.789"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
                Numéro IPI
              </label>
              <Input
                data-testid="settings-legal-ipi"
                value={settings.legal_ipi_number}
                onChange={(e) => setSettings({...settings, legal_ipi_number: e.target.value})}
                placeholder="123456"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
                Compagnie d'assurance
              </label>
              <Input
                data-testid="settings-legal-insurance"
                value={settings.legal_insurance_company}
                onChange={(e) => setSettings({...settings, legal_insurance_company: e.target.value})}
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
              Numéro de police d'assurance
            </label>
            <Input
              data-testid="settings-legal-policy"
              value={settings.legal_insurance_policy}
              onChange={(e) => setSettings({...settings, legal_insurance_policy: e.target.value})}
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">
              Texte complet des mentions légales (optionnel)
            </label>
            <Textarea
              data-testid="settings-legal-full"
              value={settings.legal_full_text || ''}
              onChange={(e) => setSettings({...settings, legal_full_text: e.target.value})}
              rows={8}
              placeholder="Ajoutez ici tout texte supplémentaire pour vos mentions légales..."
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-sm uppercase tracking-widest font-semibold"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AdminSettingsPage;
